import { useAuthState } from '@auth/hooks';
import { SlideSheet, InlineTabs, type TabItem, Icon, IconSize, Favorite, Folder, Settings } from '@soteria/design-system-ve';
import { useState, useMemo, useCallback } from 'react';
import { useParams } from 'react-router-dom';

import * as styles from './Guide.css';
import { GuideContent } from './GuideContent';
import { GuideFooter } from './GuideFooter';
import type { GuideCommunity } from './useCommunityGroupsWithCommunities';
import { useCommunityGroupsWithCommunities } from './useCommunityGroupsWithCommunities';
import { useContextAwareNavigation } from './useContextAwareNavigation';

interface GuideProps {
  isOpen: boolean;
  onClose: () => void;
}

interface GuideState {
  activeTab: number;
  selectedCommunity: GuideCommunity | null;
  userNavigatedAway: boolean;
}

// Tab identifiers
const TAB_COMMUNITIES = 1;

/**
 * Guide component
 * Xbox-inspired navigation with drill-down pattern
 * Default navigation paradigm for Soteria UI
 */
export function Guide({ isOpen, onClose }: GuideProps) {
  // Check if user is system admin for System tab visibility
  const { isUserSystemAdmin } = useAuthState();

  // Get communityId and groupId from URL params for context-aware navigation
  const { communityId, groupId } = useParams<{ communityId?: string; groupId?: string }>();

  // Fetch community groups with nested communities using optimized query
  const { data: communityGroups, loading, errors } = useCommunityGroupsWithCommunities();

  // Consolidated state management
  const [state, setState] = useState<GuideState>(() => ({
    activeTab: TAB_COMMUNITIES,
    selectedCommunity: null,
    userNavigatedAway: false,
  }));

  // Derived values (no state needed)
  const currentView = state.selectedCommunity ? 'pages' : 'groups';
  const selectedCommunityGroup = useMemo(
    () => (state.selectedCommunity ? communityGroups.find(group => group.communities.some(c => c.id === state.selectedCommunity?.id)) : null),
    [state.selectedCommunity, communityGroups]
  );

  // Tab configuration with Favorites always visible and conditional System tab
  const guideTabs: TabItem[] = useMemo(() => {
    const tabs: TabItem[] = [];

    // Favorites tab (always visible)
    tabs.push({
      label: 'Favorites',
      icon: <Icon icon={Favorite} iconSize={IconSize.XSmall} />,
      'aria-label': 'View your favorite pages',
    });

    // Communities tab (always visible)
    tabs.push({
      label: 'Communities',
      icon: <Icon icon={Folder} iconSize={IconSize.XSmall} />,
      'aria-label': 'View community groups and pages',
    });

    // System tab (admin only)
    if (isUserSystemAdmin) {
      tabs.push({
        label: 'System',
        icon: <Icon icon={Settings} iconSize={IconSize.XSmall} />,
        'aria-label': 'System administration settings',
      });
    }

    return tabs;
  }, [isUserSystemAdmin]);

  // Context-aware navigation
  const setSelectedCommunity = useCallback((community: GuideCommunity | null) => {
    setState(prev => ({ ...prev, selectedCommunity: community }));
  }, []);

  useContextAwareNavigation({
    communityId,
    groupId,
    communityGroups,
    loading,
    selectedCommunity: state.selectedCommunity,
    userNavigatedAway: state.userNavigatedAway,
    setSelectedCommunity,
  });

  // Calculate the Communities tab index (always 1 since Favorites is always at 0)
  const communitiesTabIndex = TAB_COMMUNITIES;

  // Event handlers
  const handleTabChange = useCallback(
    (tabIndex: number) => {
      setState(prev => ({
        ...prev,
        activeTab: tabIndex,
        // Reset to groups view when switching to Communities tab
        selectedCommunity: tabIndex === communitiesTabIndex ? null : prev.selectedCommunity,
      }));
    },
    [communitiesTabIndex]
  );

  const handleCommunitySelect = useCallback((community: GuideCommunity) => {
    setState(prev => ({
      ...prev,
      selectedCommunity: community,
      userNavigatedAway: true,
    }));
  }, []);

  const handleBack = useCallback(() => {
    setState(prev => ({
      ...prev,
      selectedCommunity: null,
      userNavigatedAway: true,
    }));
  }, []);

  const handleClose = useCallback(() => {
    setState(prev => ({
      ...prev,
      userNavigatedAway: false,
      // Don't reset selectedCommunity if we're on a community page
      ...(communityId ? {} : { selectedCommunity: null }),
    }));
    onClose();
  }, [communityId, onClose]);

  return (
    <SlideSheet
      darkHeader
      className={styles.guideSlideSheet}
      contentClassName={styles.guideContentOverride}
      customHeader={<div />}
      isOpen={isOpen}
      side="left"
      size="sm"
      onClose={handleClose}
    >
      <div className={styles.guideContent}>
        {/* Tab Navigation */}
        <div className={styles.tabsContainer}>
          <InlineTabs
            aria-label="Guide navigation tabs"
            size="medium"
            tabs={guideTabs}
            value={state.activeTab}
            variant="hero"
            onValueChange={handleTabChange}
          />
        </div>

        {/* Tab Content */}
        <div className={styles.tabContent}>
          <GuideContent
            activeTab={state.activeTab}
            communityGroupId={selectedCommunityGroup?.id ?? null}
            communityGroups={communityGroups}
            communityId={communityId}
            currentView={currentView}
            errors={errors}
            isSystemAdmin={isUserSystemAdmin}
            loading={loading}
            selectedCommunity={state.selectedCommunity}
            selectedGroupId={groupId}
            onBack={handleBack}
            onClose={onClose}
            onCommunitySelect={handleCommunitySelect}
          />
        </div>

        {/* Footer - persists across all tabs */}
        <GuideFooter />
      </div>
    </SlideSheet>
  );
}
